import { useMemo, useRef, useState } from 'react'
import { Popup, useControllableValue, useEscapeKey, useOnClickOutside } from '@/ui/kit'
import { cn } from './internal/utils'
import { BORDER, RADIUS } from './internal/presets'
import Input from './Input'

/**
 * antd AutoComplete: a free-text input with a suggestion list. Unlike Select, the
 * typed value is the value, and picking a suggestion only fills it in.
 */
const AutoComplete = ({
  value,
  defaultValue,
  onChange,
  onSelect,
  onSearch,
  options = [],
  filterOption = true,
  placeholder,
  allowClear = false,
  disabled = false,
  status,
  notFoundContent,
  dropdownMatchSelectWidth = true,
  className,
  style,
  children,
  ...rest
}) => {
  const [text, setText] = useControllableValue(value, defaultValue ?? '')
  const [open, setOpen] = useState(false)
  const anchorRef = useRef(null)
  const popupRef = useRef(null)

  useOnClickOutside([anchorRef, popupRef], () => setOpen(false), open)
  useEscapeKey(() => setOpen(false), open)

  const filtered = useMemo(() => {
    if (filterOption === false || !text) return options
    const predicate =
      typeof filterOption === 'function'
        ? (option) => filterOption(text, option)
        : (option) =>
            String(option.label ?? option.value)
              .toLowerCase()
              .includes(String(text).toLowerCase())
    return options.filter(predicate)
  }, [filterOption, options, text])

  const handleChange = (event) => {
    const next = event?.target?.value ?? ''
    setText(next)
    onChange?.(next)
    onSearch?.(next)
    setOpen(true)
  }

  const pick = (option) => {
    setText(option.value)
    onChange?.(option.value, option)
    onSelect?.(option.value, option)
    setOpen(false)
  }

  return (
    <div className={cn('ui-select ui-select-auto-complete relative', className)} style={style}>
      <div ref={anchorRef}>
        {children ? (
          children
        ) : (
          <Input
            value={text}
            onChange={handleChange}
            onFocus={() => setOpen(true)}
            placeholder={placeholder}
            disabled={disabled}
            status={status}
            allowClear={allowClear}
            role="combobox"
            aria-expanded={open}
            aria-autocomplete="list"
            {...rest}
          />
        )}
      </div>

      <Popup
        anchorRef={anchorRef}
        popupRef={popupRef}
        open={open && (filtered.length > 0 || Boolean(notFoundContent))}
        placement="bottomLeft"
        matchAnchorWidth={dropdownMatchSelectWidth !== false}
        className={cn(
          'ui-select-dropdown max-h-64 overflow-auto border bg-[var(--surface-card)] py-1 shadow-[var(--shadow-md)]',
          BORDER,
          RADIUS
        )}
      >
        {filtered.length === 0 ? (
          <div className="px-3 py-2 text-sm text-[var(--text-muted)]">{notFoundContent}</div>
        ) : (
          <ul role="listbox" className="m-0 list-none p-0">
            {filtered.map((option) => (
              <li key={String(option.value)} role="none">
                <div
                  role="option"
                  aria-selected={option.value === text}
                  tabIndex={-1}
                  onClick={() => pick(option)}
                  onKeyDown={(event) => {
                    if (event.key !== 'Enter' && event.key !== ' ') return
                    event.preventDefault()
                    pick(option)
                  }}
                  className="ui-select-item-option flex min-h-[40px] cursor-pointer items-center px-3 py-2 text-sm hover:bg-[var(--color-primary-50)]"
                >
                  {option.label ?? option.value}
                </div>
              </li>
            ))}
          </ul>
        )}
      </Popup>
    </div>
  )
}

AutoComplete.Option = ({ children }) => children

export default AutoComplete
